import { useCallback } from "react";
import { ScrollingTextArea } from "@bagel-web/components";

import {
  CommonKVStoreState,
  CommonPuppeteerKVStoreState,
} from "../../../types";
import {
  ASHComponentVisualization,
  ASHComponentVisualizationProps,
} from "../ASHComponentCard";
import { StickyHeader } from "../SimpleComponentVisualization";
import { useAgentViewPreferences } from "../../viewPreferences/agentViewPreferences";
import AddNodeRow from "./AddNodeRow";
import KVNodeView, { NodeTreeView } from "./KVNodeView";
import KVStoreToolbar from "./KVStoreToolbar";
import { NodeFilters } from "./kvStoreNodes";
import { applyKVNodeEdit, deleteKVSubtree, KVNodeEdit } from "./kvStoreSeed";
import useNodeAdding from "./useNodeAdding";
import useNodeDisplayMap from "./useNodeDisplayMap";
import useNodeEditing from "./useNodeEditing";

const KVStoreVisualization: ASHComponentVisualization<
  CommonKVStoreState | CommonPuppeteerKVStoreState
> = ({
  componentPresentation,
  componentState,
  sessionId,
  agentId,
  agencyTagFilter,
  isEditMode,
  messages = [],
  onMessagesChange,
}: ASHComponentVisualizationProps<
  CommonKVStoreState | CommonPuppeteerKVStoreState
>) => {
  const kvStoreState = componentState?.state;
  const delimiter = kvStoreState?.delimiter ?? null;

  // Each kv seed message holds one node's value under its full key: an edit
  // rewrites that message (or seeds one for a structural node), a rename
  // moves the subtree's messages with it, and a delete takes the subtree.
  const editNode = useCallback(
    (key: string, edit: KVNodeEdit) =>
      onMessagesChange?.(applyKVNodeEdit(messages, key, edit, delimiter)),
    [messages, onMessagesChange, delimiter]
  );
  const deleteNode = (key: string) =>
    onMessagesChange?.(deleteKVSubtree(messages, key, delimiter));

  const editor = useNodeEditing(editNode);
  const adder = useNodeAdding(editNode, delimiter);

  const { showCompactStateContent, getComponentTextFilter } =
    useAgentViewPreferences();
  const keyFilterId = componentState?.id
    ? `${componentState.id}-key`
    : undefined;
  const valueFilterId = componentState?.id
    ? `${componentState.id}-value`
    : undefined;
  const filters: NodeFilters = {
    agencyTagFilter,
    keyFilter: getComponentTextFilter(keyFilterId),
    valueFilter: getComponentTextFilter(valueFilterId),
  };
  const { displayMap, setNodeCollapseState } = useNodeDisplayMap(
    kvStoreState?.data,
    filters
  );

  const view: NodeTreeView = {
    componentPresentation,
    compact: showCompactStateContent,
    delimiter,
    sessionId,
    agentId,
    isEditMode,
    filters,
    displayMap,
    setNodeCollapseState,
    editor,
    adder,
    onDeleteNode: deleteNode,
  };

  return (
    <>
      <KVStoreToolbar
        keyFilterId={keyFilterId}
        valueFilterId={valueFilterId}
        isEditMode={isEditMode}
        onAdd={() => adder.startAdding(null)}
      />
      <ScrollingTextArea className="font-monospace" $height="1000px">
        <table className="table table-striped">
          <StickyHeader>
            <tr>
              <th>Key</th>
              <th>Value</th>
              <th>Moment</th>
            </tr>
          </StickyHeader>
          <tbody>
            {adder.adding?.parentKey === null && (
              <AddNodeRow
                depth={0}
                adder={adder}
                compact={showCompactStateContent}
              />
            )}
            {Object.entries(kvStoreState?.data ?? {}).map(([key, node]) => (
              <KVNodeView
                key={key}
                view={view}
                k={key}
                fullKey={key}
                node={node}
                depth={0}
              />
            ))}
          </tbody>
        </table>
      </ScrollingTextArea>
    </>
  );
};

export default KVStoreVisualization;
